import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "sonner";
import { apiClient } from "@/common/apiClient";

export const CreateKPIQuestionList = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [questionsText, setQuestionsText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const generateTestData = () => {
    setTitle("Q1 2024 Customer Satisfaction Survey");
    setDescription("Quarterly customer feedback and satisfaction assessment");
    setQuestionsText(`How satisfied are you with our overall service?
How likely are you to recommend us to others?
What can we improve in our customer support?
How would you rate the quality of our products?
How easy was it to complete your recent purchase?
Did you find our website user-friendly?
How satisfied are you with our delivery speed?
Would you purchase from us again?`);
    toast.success("Test data generated!");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const questions = questionsText
        .split("\n")
        .filter((line) => line.trim())
        .map((text) => ({ text: text.trim() }));

      if (questions.length === 0) {
        toast.error("Please add at least one question");
        return;
      }

      const response = await apiClient.post("/api/kpi-question-list", {
        title,
        description,
        questions,
      });

      if (response.success) {
        toast.success("Question list created successfully!");
        setTitle("");
        setDescription("");
        setQuestionsText("");
      } else {
        toast.error("Failed to create question list: " + (response.message || "Unknown error"));
      }
    } catch (error) {
      toast.error("Failed to create question list: " + (error as any).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Create KPI Question List</CardTitle>
          <CardDescription>
            Create a new question list for KPI surveys
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder="e.g., Q1 Customer Satisfaction Survey"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Input
                id="description"
                placeholder="Brief description of this survey"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="questions">Questions (One per line)</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={generateTestData}
                >
                  Generate Test Data
                </Button>
              </div>
              <Textarea
                id="questions"
                placeholder="How satisfied are you with our service?
What can we improve?
Would you recommend us to others?"
                value={questionsText}
                onChange={(e) => setQuestionsText(e.target.value)}
                className="min-h-[200px] font-mono"
                required
              />
              <p className="text-sm text-muted-foreground">
                Enter each question on a new line
              </p>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full"
              variant="default"
            >
              {isSubmitting ? "Creating..." : "Create Question List"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
